/**
 * Вход и регистрация: логин и пароль либо внешний провайдер (Google, GitHub, Яндекс, Microsoft,
 * Discord — те, что настроены на сервере). Администратор входит в ту же форму своим логином.
 * Также обрабатывает возврат от провайдера: /auth/complete#token=… | #linked=… | #error=….
 */
import { useEffect, useState, type FormEvent } from 'react';
import type { Msg } from '@mt/protocol';
import { t, tm } from '../../i18n';
import { api } from '../../lib/api';
import { navigate } from '../../lib/router';
import { client, useApp } from '../../net/client';
import { LangSwitch } from '../LangSwitch';
import { ProviderIcon, providerTitle } from './ProviderIcon';

type Mode = 'login' | 'register';

/** Стартовый баланс до получения сведений о сервере (10 000 кр. в минорных единицах). */
const DEFAULT_STARTING_BALANCE = 1_000_000;
/** Коды «сессия не найдена/истекла» — обычный выход из сессии, ошибкой на форме не показываются. */
const SESSION_GONE = new Set(['err.sessionExpired', 'err.sessionNotFound']);

export function LoginPage() {
  const info = useApp((s) => s.info);
  const authError = useApp((s) => s.authError);
  const authMsg = useApp((s) => s.authMsg);
  const [mode, setMode] = useState<Mode>('login');
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const providers = info?.auth.providers ?? [];

  useEffect(() => setError(null), [mode]);
  // сообщение сервера о завершённой сессии («сессия не найдена», блокировка) показываем один раз
  useEffect(() => {
    if (!authError && !authMsg) return;
    const gone = authMsg ? SESSION_GONE.has(authMsg.code) : /сессия не найдена/.test(authError ?? '');
    if (!gone) setError(tm(authMsg, authError ?? ''));
  }, [authError, authMsg]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (mode === 'register' && password !== password2) return setError(t('auth.mismatch'));
    setBusy(true);
    try {
      const r = mode === 'login' ? await api.login(login.trim(), password) : await api.register(login.trim(), password, name.trim());
      client.signIn(r.token);
      navigate(r.role === 'admin' ? '/admin' : '/', { replace: true });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const sso = async (id: string) => {
    setBusy(true);
    try {
      location.assign((await api.oauthStart(id)).url);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  const valid = mode === 'login' ? login.trim() && password : login.trim().length >= 3 && password.length >= 8 && name.trim().length >= 2 && password2;

  return (
    <div className="auth-page">
      <LangSwitch className="auth-lang" />
      <section className="auth-intro">
        <h1>{t('app.brand')}</h1>
        <p className="lead">{t('auth.lead')}</p>
        <ul className="auth-points">
          <li>
            <b>{t('auth.point1b')}</b> {t('auth.point1')}
          </li>
          <li>
            <b>{t('auth.point2b')}</b> {t('auth.point2')}
          </li>
          <li>
            <b>{t('auth.point3b')}</b> {t('auth.point3')}
          </li>
        </ul>
        <p className="demo-note">{t('auth.demo', { amount: info?.startingBalance ?? DEFAULT_STARTING_BALANCE })}</p>
      </section>

      <form className="panel auth-card" onSubmit={submit} noValidate>
        <div className="segmented auth-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={mode === 'login'} className={mode === 'login' ? 'on' : ''} onClick={() => setMode('login')}>
            {t('auth.tabLogin')}
          </button>
          <button type="button" role="tab" aria-selected={mode === 'register'} className={mode === 'register' ? 'on' : ''} onClick={() => setMode('register')}>
            {t('auth.tabRegister')}
          </button>
        </div>

        <label className="field">
          {t('auth.login')}
          <input autoFocus autoComplete="username" value={login} maxLength={32} onChange={(e) => setLogin(e.target.value)} placeholder={t('auth.loginPh')} />
        </label>
        {mode === 'register' && (
          <label className="field">
            {t('auth.name')}
            <input autoComplete="nickname" value={name} maxLength={24} onChange={(e) => setName(e.target.value)} placeholder={t('auth.namePh')} />
          </label>
        )}
        <label className="field">
          {t('auth.password')}
          <input type="password" autoComplete={mode === 'login' ? 'current-password' : 'new-password'} value={password} maxLength={128} onChange={(e) => setPassword(e.target.value)} />
          {mode === 'register' && <small className="field-hint">{t('auth.passwordHint')}</small>}
        </label>
        {mode === 'register' && (
          <label className="field">
            {t('auth.password2')}
            <input type="password" autoComplete="new-password" value={password2} maxLength={128} onChange={(e) => setPassword2(e.target.value)} />
          </label>
        )}
        {error && (
          <p className="notice danger" role="alert">
            {error}
          </p>
        )}
        <button className="btn primary wide" disabled={busy || !valid}>
          {mode === 'login' ? t('auth.submitLogin') : t('auth.submitRegister')}
        </button>

        {providers.length > 0 && (
          <>
            <div className="auth-sep">
              <span>{t('auth.or')}</span>
            </div>
            <div className="sso-grid">
              {providers.map((p) => (
                <button key={p.id} type="button" className="btn sso" disabled={busy} onClick={() => void sso(p.id)}>
                  <ProviderIcon id={p.id} />
                  {providerTitle(p)}
                </button>
              ))}
            </div>
          </>
        )}
      </form>
    </div>
  );
}

/** Возврат от провайдера (/auth/complete#…): вход, привязка или ошибка. */
export function useAuthComplete(): void {
  useEffect(() => {
    if (location.pathname !== '/auth/complete') return;
    const h = new URLSearchParams(location.hash.slice(1));
    history.replaceState(null, '', '/auth/complete');
    const token = h.get('token');
    const linked = h.get('linked');
    const error = h.get('error');
    // msg — код сообщения сервера (JSON) для показа на выбранном языке; error — русский текст на случай старого сервера
    let msg: Msg | null = null;
    try {
      const raw = h.get('msg');
      if (raw) msg = JSON.parse(raw) as Msg;
    } catch {
      msg = null;
    }
    if (token) {
      client.signIn(token);
      if (h.get('created')) client.toast('info', t('auth.created'));
      navigate('/', { replace: true });
    } else if (linked) {
      // сервер присылает название провайдера (по-русски) — переводим его через сведения о провайдерах
      const p = client.state.info?.auth.providers.find((x) => x.title === linked);
      client.toast('info', t('auth.linked', { provider: p ? providerTitle(p) : linked }));
      navigate('/profile', { replace: true });
    } else {
      client.toast('error', tm(msg, error ?? t('auth.failed')));
      navigate(client.state.profile ? '/profile' : '/', { replace: true });
    }
  }, []);
}
